import { useState } from "react";
import { Building2, Plus, Search, Phone, Globe, User } from "lucide-react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { useToast } from "@/hooks/use-toast";
import { OrganizationModal } from "@/components/crm/OrganizationModal";
import { ContactModal } from "@/components/crm/ContactModal";
import { useAuth } from "@/hooks/useAuth";

interface Contact {
  id: string;
  first_name: string | null;
  last_name: string | null;
  email: string | null;
  phone: string | null;
  title: string | null;
  consent_gdpr: boolean;
  created_at: string;
  updated_at: string;
}

interface Organization {
  id: string;
  name: string;
  status: string;
  classification: string;
  sector: string | null;
  website: string | null;
  phone: string | null;
  addresses: any[];
  created_at: string;
  logo_url?: string | null;
  favicon_url?: string | null;
  contacts?: Contact[];
}

export default function ChargingOrganisaties() {
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedOrg, setSelectedOrg] = useState<Organization | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedContact, setSelectedContact] = useState<Contact | null>(null);
  const [isContactModalOpen, setIsContactModalOpen] = useState(false);
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { userRole } = useAuth();
  const isChargingUser = userRole === 'charging_user';

  // Helper to get organization logo/favicon
  const getOrgFavicon = (org: Organization) => {
    if (org.logo_url) return org.logo_url;
    if (org.favicon_url) return org.favicon_url;
    if (org.website) {
      const domain = org.website.replace(/^https?:\/\//, '').replace(/^www\./, '').split('/')[0];
      return `https://www.google.com/s2/favicons?sz=64&domain=${domain}`;
    }
    return null;
  };

  // Fetch organizations with contacts (exclude soft-deleted)
  const { data: organizations = [], isLoading } = useQuery({
    queryKey: ['charging-organizations'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('organizations')
        .select(`
          id, name, status, classification, sector, website, phone, addresses, created_at,
          logo_url, favicon_url,
          contacts(id, first_name, last_name, email, phone, title, consent_gdpr, created_at, updated_at)
        `)
        .is('deleted_at', null)
        .order('name');

      if (error) throw error;
      return data as Organization[];
    }
  });

  // Delete mutation - soft delete for charging users, hard delete for CRM users
  const deleteMutation = useMutation({
    mutationFn: async (orgId: string) => {
      if (isChargingUser) {
        // Soft delete for charging users
        const { error } = await supabase
          .from('organizations')
          .update({ deleted_at: new Date().toISOString() })
          .eq('id', orgId);
        if (error) throw error;
      } else {
        // Hard delete for CRM users
        const { error } = await supabase
          .from('organizations')
          .delete()
          .eq('id', orgId);
        if (error) throw error;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['charging-organizations'] });
      toast({
        title: isChargingUser ? "Organisatie verborgen" : "Organisatie verwijderd",
        description: isChargingUser 
          ? "De organisatie is verborgen. CRM gebruikers kunnen deze nog zien." 
          : "De organisatie is permanent verwijderd."
      });
    },
    onError: (error) => {
      toast({
        title: "Fout bij verwijderen",
        description: error.message,
        variant: "destructive"
      });
    }
  });

  // Filter organizations
  const filteredOrgs = organizations.filter(org =>
    org.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    org.sector?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleAdd = () => {
    setSelectedOrg(null);
    setIsModalOpen(true);
  };

  const handleEdit = (org: Organization) => {
    setSelectedOrg(org);
    setIsModalOpen(true);
  };

  const handleDelete = async (orgId: string) => {
    const confirmMessage = isChargingUser 
      ? 'Weet je zeker dat je deze organisatie wilt verbergen? CRM gebruikers kunnen deze nog steeds zien.'
      : 'Weet je zeker dat je deze organisatie permanent wilt verwijderen?';
    if (confirm(confirmMessage)) {
      deleteMutation.mutate(orgId);
    }
  };

  const getStatusBadge = (status: string) => {
    const variants: Record<string, { variant: "default" | "secondary" | "outline"; label: string }> = {
      prospect: { variant: "secondary", label: "Prospect" },
      customer: { variant: "default", label: "Klant" },
      former: { variant: "outline", label: "Voormalig" },
    };
    const config = variants[status] || { variant: "outline", label: status };
    return <Badge variant={config.variant}>{config.label}</Badge>;
  };

  const getClassificationBadge = (classification: string) => {
    const variants: Record<string, { variant: "default" | "secondary" | "outline"; label: string }> = {
      klant: { variant: "default", label: "Klant" },
      prospect: { variant: "secondary", label: "Prospect" },
      partner: { variant: "outline", label: "Partner" },
      leverancier: { variant: "outline", label: "Leverancier" },
    };
    const config = variants[classification] || { variant: "outline", label: classification };
    return <Badge variant={config.variant}>{config.label}</Badge>;
  };

  if (isLoading) {
    return (
      <div className="container mx-auto p-8">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-muted rounded w-1/4"></div>
          <div className="grid grid-cols-3 gap-4">
            {[1, 2, 3].map(i => (
              <div key={i} className="h-48 bg-muted rounded"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-8">
      <div className="mb-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-foreground flex items-center gap-2">
              <Building2 className="h-8 w-8" />
              Organisaties
            </h1>
            <p className="text-muted-foreground mt-2">
              Beheer organisaties zichtbaar voor Greenvision Charging
            </p>
          </div>
          <Button onClick={handleAdd}>
            <Plus className="h-4 w-4 mr-2" />
            Nieuwe Organisatie
          </Button>
        </div>
      </div>

      <div className="mb-6">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Zoek op naam of sector..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10 max-w-md"
          />
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {filteredOrgs.map((org) => {
          const favicon = getOrgFavicon(org);
          return (
            <Card key={org.id} className="hover:shadow-lg transition-shadow">
              <CardHeader>
                <div className="flex items-center gap-3">
                  <Avatar className="h-10 w-10">
                    {favicon && (
                      <AvatarImage 
                        src={favicon} 
                        alt="" 
                        className="object-contain"
                        onError={(e) => {
                          e.currentTarget.style.display = 'none';
                        }}
                      />
                    )}
                    <AvatarFallback className="bg-primary text-primary-foreground text-sm">
                      {org.name.substring(0, 2).toUpperCase()}
                    </AvatarFallback>
                  </Avatar>
                  <CardTitle className="truncate flex-1">{org.name}</CardTitle>
                </div>
                <div className="flex gap-2 mt-2">
                  {getStatusBadge(org.status)}
                  {getClassificationBadge(org.classification)}
                </div>
              </CardHeader>
              <CardContent className="space-y-3">
                {org.website && (
                  <div className="flex items-center gap-2 text-sm">
                    <Globe className="h-4 w-4 text-muted-foreground" />
                    <a 
                      href={org.website.startsWith('http') ? org.website : `https://${org.website}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-primary hover:underline truncate"
                    >
                      {org.website.replace(/^https?:\/\//, '')}
                    </a>
                  </div>
                )}

                {/* Contacts - clickable */}
                {org.contacts && org.contacts.length > 0 && (
                  <div className="border-t pt-2 mt-2">
                    <p className="text-xs text-muted-foreground mb-1 font-medium flex items-center gap-1">
                      <User className="h-3 w-3" />
                      Contactpersonen ({org.contacts.length})
                    </p>
                    <div className="space-y-1">
                      {org.contacts.slice(0, 3).map((contact) => (
                        <div 
                          key={contact.id} 
                          className="text-sm text-primary hover:underline cursor-pointer truncate"
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedContact(contact);
                            setIsContactModalOpen(true);
                          }}
                        >
                          {contact.first_name} {contact.last_name}
                        </div>
                      ))}
                      {org.contacts.length > 3 && (
                        <p className="text-xs text-muted-foreground">
                          +{org.contacts.length - 3} meer
                        </p>
                      )}
                    </div>
                  </div>
                )}

                {org.phone && (
                  <div className="flex items-center gap-2 text-sm">
                    <Phone className="h-4 w-4 text-muted-foreground" />
                    <a href={`tel:${org.phone}`} className="text-primary hover:underline">
                      {org.phone}
                    </a>
                  </div>
                )}

                <div className="flex gap-2 pt-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleEdit(org)}
                    className="flex-1"
                  >
                    Bewerken
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleDelete(org.id)}
                    className="flex-1"
                  >
                    {isChargingUser ? 'Verbergen' : 'Verwijderen'}
                  </Button>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {filteredOrgs.length === 0 && (
        <div className="text-center py-12">
          <Building2 className="h-16 w-16 mx-auto mb-4 text-muted-foreground" />
          <h2 className="text-xl font-semibold mb-2">Geen organisaties gevonden</h2>
          <p className="text-muted-foreground mb-4">
            {searchTerm ? 'Probeer een andere zoekterm' : 'Voeg je eerste organisatie toe'}
          </p>
          {!searchTerm && (
            <Button onClick={handleAdd}>
              <Plus className="h-4 w-4 mr-2" />
              Nieuwe Organisatie
            </Button>
          )}
        </div>
      )}

      <OrganizationModal
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          setSelectedOrg(null);
        }}
        organization={selectedOrg}
        onSave={() => {
          queryClient.invalidateQueries({ queryKey: ['charging-organizations'] });
        }}
      />

      <ContactModal
        isOpen={isContactModalOpen}
        onClose={() => {
          setIsContactModalOpen(false);
          setSelectedContact(null);
        }}
        contact={selectedContact ? { 
          id: selectedContact.id, 
          first_name: selectedContact.first_name || '', 
          last_name: selectedContact.last_name || '', 
          email: selectedContact.email || '',
          phone: selectedContact.phone || '',
          title: selectedContact.title || '',
          consent_gdpr: selectedContact.consent_gdpr ?? false,
          created_at: selectedContact.created_at,
          updated_at: selectedContact.updated_at
        } : null}
        onSave={() => {
          queryClient.invalidateQueries({ queryKey: ['charging-organizations'] });
        }}
      />
    </div>
  );
}
